import { resolveOtherServiceCustomerAmounts } from '../../../../../utils/otherServicePricing';

const finiteMoney = (value) => {
  if (value == null || value === "") return null;
  const numeric = Number(String(value).replace(/[$,%]/g, ""));
  return Number.isFinite(numeric) ? Math.round(numeric * 100) / 100 : null;
};

const roundMoney = (value) => Math.round(value * 100) / 100;

const roundPercentage = (value) => Math.round(value * 100) / 100;

// Keep the project overview's booked-source policy identical to the
// commission resolver: customer agreement AND an approved Sales Order status.
// A newer draft/regeneration is not an authoritative booked financial source.
const BOOKED_APPROVED_SO_STATUSES = new Set(["3", "5", "6", "7", "8", "9"]);

export const isApprovedBookedSalesOrder = (row = {}) => {
  const agreed = row?.customerAgreed;
  const status = String(row?.status ?? row?.Status ?? "").trim();
  return (agreed === true || agreed === 1 || agreed === "1") &&
    BOOKED_APPROVED_SO_STATUSES.has(status);
};

export const selectLatestApprovedBookedSalesOrder = (rows = [], orderNumber = null) => {
  const eligible = (Array.isArray(rows) ? rows : []).filter((row) => (
    isApprovedBookedSalesOrder(row) &&
    (orderNumber == null || row?.orderId == null || String(row.orderId) === String(orderNumber))
  ));
  const dateValue = (value) => {
    const parsed = Date.parse(value || "");
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return [...eligible].sort((a, b) => (
    dateValue(b.updatedAt) - dateValue(a.updatedAt) ||
    dateValue(b.createdAt) - dateValue(a.createdAt) ||
    (Number(b.estimationId || 0) - Number(a.estimationId || 0))
  ))[0] || null;
};

export const calculateBookedMarginPct = (revenue, margin) => {
  const bookedRevenue = finiteMoney(revenue);
  const bookedMargin = finiteMoney(margin);
  return bookedRevenue != null && bookedRevenue > 0 && bookedMargin != null
    ? roundPercentage((bookedMargin / bookedRevenue) * 100)
    : null;
};

export const mergeBookedStampSummary = (payload = {}, summary = {}) => ({
  ...summary,
  subtotal: summary?.subtotal ?? payload?.targetRevenue ?? payload?.revenue ?? payload?.subtotal,
  targetRevenue: summary?.targetRevenue ?? payload?.targetRevenue,
  targetCosts: summary?.targetCosts ?? payload?.targetCosts,
  costs: summary?.costs ?? payload?.costs,
  bookedMarginAmount:
    summary?.bookedMarginAmount ?? summary?.bookedMargin ?? payload?.bookedMarginAmount ?? payload?.bookedMargin,
  bookedMargin: summary?.bookedMargin ?? payload?.bookedMargin,
  bookedMarginPct: summary?.bookedMarginPct ?? payload?.bookedMarginPct,
  totalInclTax:
    summary?.totalInclTax ??
    summary?.totalIncl ??
    payload?.totalInclTax ??
    payload?.totalIncl ??
    payload?.invoiceableTotal ??
    payload?.salesOrderTotal,
});

export const resolveBookedSummaryFinancials = (
  summary = {},
  source = "sales-order-cart-summary"
) => {
  const revenue = finiteMoney(summary?.subtotal ?? summary?.targetRevenue ?? summary?.revenue);
  if (revenue == null || revenue === 0) return null;

  const explicitMargin = finiteMoney(summary?.bookedMarginAmount ?? summary?.bookedMargin);
  const explicitCosts = finiteMoney(summary?.targetCosts ?? summary?.costs);
  const validMargin = explicitMargin != null && explicitMargin >= 0 && explicitMargin <= revenue;
  const hasAuthoritativeCosts = explicitCosts != null && explicitCosts > 0;

  // A revenue-only or placeholder-zero-cost stamp is not a complete booked
  // financial record. Do not manufacture 100% margin from missing COGS.
  if (!hasAuthoritativeCosts && !validMargin) return null;

  // Legacy Sales Order stamps sometimes persisted targetCosts=0 alongside a
  // valid margin. Repair that booked source directly; never consult the live cart.
  const shouldDeriveCostsFromMargin = validMargin && !hasAuthoritativeCosts && revenue > 0;

  let costs;
  let margin;

  if (hasAuthoritativeCosts) {
    costs = explicitCosts;
    margin = roundMoney(revenue - costs);
  } else if (validMargin) {
    margin = explicitMargin;
    costs = roundMoney(revenue - margin);
  } else {
    costs = explicitCosts ?? 0;
    margin = roundMoney(revenue - costs);
  }

  const marginPct = calculateBookedMarginPct(revenue, margin);
  const totalInclTax = finiteMoney(summary?.totalInclTax ?? summary?.totalIncl) ?? revenue;

  return {
    source,
    bookedRevenue: revenue,
    costs,
    bookedMargin: margin,
    bookedMarginPct: marginPct,
    totalInclTax,
    costsDerivedFromBookedMargin: shouldDeriveCostsFromMargin,
    snapshotSummary: {
      subtotal: revenue,
      bookedMarginAmount: margin,
      bookedMarginPct: marginPct,
      totalInclTax,
    },
  };
};

/**
 * Resolve booked totals from the approved Sales Order's OWN frozen cart rows.
 *
 * Regenerated legacy stamps can contain a new orderSummary/totals subtotal and
 * an older bookedMargin. The stamp margin must not override a complete frozen
 * revenue/cost calculation. Never read the live order cart or rewrite the
 * Sales Order, commissions, or historical stamps here.
 *
 * Fail closed if any row cost is unknown or if row revenue differs from the
 * stamped subtotal. An incomplete snapshot is NOT evidence of 100% margin.
 */
export const resolveFrozenSalesOrderBookedFinancials = (salesOrder = {}) => {
  if (!isApprovedBookedSalesOrder(salesOrder)) return null;
  const parse = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    try { return JSON.parse(value); } catch { return null; }
  };
  const stamp = parse(salesOrder?.stampedBookedAmount);
  const snapshot = parse(stamp?.cartSnapshot);
  if (!stamp || !snapshot) return null;
  // Require any persisted snapshot identity to match this exact frozen copy.
  // A missing ID/hash is not evidence that another revision is equivalent.
  if (stamp?.cartSnapshotId &&
      String(stamp.cartSnapshotId) !== String(snapshot?.id || '')) return null;
  if (stamp?.cartSnapshotHash &&
      String(stamp.cartSnapshotHash) !== String(snapshot?.hash || '')) return null;

  // Select one canonical alias only. Source data carries repeated
  // products/decorations in various casing variants.
  const payload = parse(snapshot?.payload) || snapshot;
  const products = [snapshot?.items, payload?.items, payload?.Products,
    payload?.products, snapshot?.Products, snapshot?.products]
    .find((rows) => Array.isArray(rows) && rows.length) || [];
  if (!products.length) return null;
  const services = [snapshot?.OtherService, snapshot?.otherServices,
    payload?.OtherService, payload?.otherServices, payload?.otherService]
    .find(Array.isArray) || [];

  const cents = (value) => Math.round(Number(value) * 100);
  const firstFinite = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;
      const amount = Number(value);
      if (Number.isFinite(amount)) return amount;
    }
    return null;
  };
  const totalRow = (row, quantity) => {
    const explicit = firstFinite(row?.Total, row?.total, row?.Subtotal, row?.subtotal);
    if (explicit != null && explicit > 0) return cents(explicit);
    const unitPrice = firstFinite(row?.Retail, row?.retail, row?.ClientPrice,
      row?.clientPrice, row?.Price, row?.price);
    if (unitPrice == null) return explicit === 0 ? 0 : null;
    return cents(unitPrice * quantity);
  };
  const rowCost = (row, quantity) => {
    const explicit = firstFinite(row?.CostTotal, row?.costTotal, row?.TotalCost, row?.totalCost);
    if (explicit != null && explicit > 0) return cents(explicit);
    const unitCost = firstFinite(row?.NetCost, row?.netCost,
      row?.UnitCost, row?.unitCost, row?.Cost, row?.cost);
    // Some legacy rows carry CostTotal: 0 alongside a real NetCost.
    // Use the saved per-unit cost, but retain explicit zero-cost lines.
    return unitCost != null ? cents(unitCost * quantity)
      : explicit === 0 ? 0 : null;
  };

  let revenueCents = 0;
  let costCents = 0;
  const addRow = (row) => {
    const qty = firstFinite(row?.Quantity, row?.quantity, row?.qty);
    if (!Number.isFinite(qty) || qty <= 0) return false;
    const revenue = totalRow(row, qty);
    const cost = rowCost(row, qty);
    if (revenue == null || cost == null || revenue < 0 || cost < 0) return false;
    revenueCents += revenue;
    costCents += cost;
    return true;
  };

  for (const product of products) {
    const priceRows = getCanonicalPricingRows(product);
    if (!Array.isArray(priceRows) || !priceRows.length) return null;
    if (priceRows.some((row) => !addRow(row))) return null;

    const decorations = product?.decorations ?? product?.Decorations ??
      product?.Decoration ?? product?.decoration ?? [];
    if (!Array.isArray(decorations)) return null;
    for (const decoration of decorations) {
      const charges = decoration?.Charge ?? decoration?.charge ??
        decoration?.Charges ?? decoration?.charges ?? [];
      if (!Array.isArray(charges)) return null;
      if (charges.some((row) => !addRow(row))) return null;
    }

    // Artwork and miscellaneous charge models vary across legacy revisions.
    // Never omit a priced row just to force a margin reconciliation.
    const artwork = product?.artworks ?? product?.Artworks ?? [];
    if (!Array.isArray(artwork)) return null;
    if (artwork.some((row) => Number(row?.Charge ?? row?.charge ?? row?.Total ?? row?.total ?? 0) !== 0)) return null;
    const extraCharges = product?.productAdditionalCharges ?? product?.ProductAdditionalCharges ?? [];
    if (Array.isArray(extraCharges) && extraCharges.length) return null;
  }

  for (const service of services) {
    if (service?.isDeleted === true || service?.isDeleted === 'true') continue;
    const amounts = resolveOtherServiceCustomerAmounts(service);
    const subtotal = Number(amounts?.subtotal);
    const quantity = Number(amounts?.quantity);
    if (!Number.isFinite(subtotal) || !Number.isFinite(quantity) ||
        quantity <= 0 || subtotal < 0) return null;
    const cost = rowCost(service, quantity);
    if (cost == null || cost < 0) return null;
    revenueCents += cents(subtotal);
    costCents += cost;
  }

  const stampedSubtotals = [stamp?.totals?.subtotal, stamp?.orderSummary?.subtotal]
    .map((value) => firstFinite(value))
    .filter((value) => value != null && value > 0);
  // Do not infer a booked basis from an incomplete stamp or an unrelated cart.
  if (!stampedSubtotals.length ||
      stampedSubtotals.some((value) => Math.abs(cents(value) - revenueCents) > 1) ||
      revenueCents <= 0) return null;

  // Cross-check the complete frozen cost against the historical stamp's
  // own cost basis. Project #295: 885.45 - 354.15 = 531.30, exactly matching
  // its frozen product + decoration + freight costs. If there are additional
  // booked costs not represented by the frozen rows, keep the older stamped
  // financials rather than quietly overstating the margin.
  const explicitCosts = firstFinite(stamp?.targetCosts, stamp?.costs);
  const oldRevenue = firstFinite(stamp?.targetRevenue, stamp?.revenue);
  const oldMargin = firstFinite(stamp?.bookedMarginAmount, stamp?.bookedMargin);
  if (explicitCosts != null && explicitCosts > 0) {
    if (Math.abs(cents(explicitCosts) - costCents) > 1) return null;
  } else if (oldRevenue != null && oldRevenue > 0 && oldMargin != null) {
    if (oldMargin < 0 || oldMargin > oldRevenue ||
        Math.abs(cents(oldRevenue - oldMargin) - costCents) > 1) return null;
  }

  const marginCents = revenueCents - costCents;
  const bookedRevenue = revenueCents / 100;
  const bookedMargin = marginCents / 100;
  const totalInclTax = firstFinite(stamp?.totals?.totalIncl, stamp?.totals?.total,
    stamp?.orderSummary?.totalIncl, stamp?.invoiceableTotal);

  return {
    source: 'approved-sales-order-frozen-cart',
    bookedRevenue,
    costs: costCents / 100,
    bookedMargin,
    bookedMarginPct: calculateBookedMarginPct(bookedRevenue, bookedMargin),
    totalInclTax: totalInclTax ?? bookedRevenue,
    costsDerivedFromBookedMargin: false,
    snapshotSummary: {
      subtotal: bookedRevenue,
      bookedMarginAmount: bookedMargin,
      bookedMarginPct: calculateBookedMarginPct(bookedRevenue, bookedMargin),
      totalInclTax: totalInclTax ?? bookedRevenue,
    },
  };
};

// Explicitly distinguish an incomplete approved booking from a real $0 sale.
// This must never be populated from the mutable Order Cart.
export const unavailableBookedFinancials = Object.freeze({
  source: "booked-unavailable",
  bookedRevenue: null,
  costs: null,
  bookedMargin: null,
  bookedMarginPct: null,
  totalInclTax: null,
});

export const selectOverviewFinancials = (candidates = [], fallback = null) =>
  candidates.find(Boolean) ||
  fallback || {
    source: "fallback-zero",
    bookedRevenue: 0,
    costs: 0,
    bookedMargin: 0,
    bookedMarginPct: null,
    totalInclTax: 0,
  };

// Cart payloads may expose the same pricing rows under multiple casing aliases.
// Consume one authoritative alias, never sum both (which doubles costs).
export const getCanonicalPricingRows = (entry = {}) =>
  entry?.pricings ?? entry?.Pricings ?? entry?.Pricing ?? entry?.pricing ?? [];
